import _ from 'lodash'; // eslint-disable-line
import moment from 'moment';
import { ActionTypes } from '../actions/cardProjectActionsV2';
import { invitationSteps, petiteSaveTheDateSteps, postcardSaveTheDateSteps } from '../util/customizationNavigation';

const orderedCustomizationTypes = ['INVITATION', 'SAVE_THE_DATE', 'RSVP', 'RSVP_ENVELOPE', 'INVITATION_ENVELOPE'];

const customizationMapper = (customization) => {
  const { elementsByUUID, pages } = customization.pages.reduce((result, page) => {
    const { elements, ...pageFields } = page;
    // handle filtering of pages where activeAt is null, these extra pages are used during toggling meal options and other
    // similar operations but should be filtered out
    if (pageFields.active_at) {
      const sortedElementUUIDs = elements
        .sort((a, b) => a.x - b.x) // sort element uuids by x values ascending so that form fields display from left to right
        .sort((a, b) => a.y - b.y) // then sort by y value ascending so that form fields display form fields display in same order that elements appear vertically
        .map(element => element.uuid);
      const formattedPage = {
        ...pageFields,
        elementUUIDs: sortedElementUUIDs,
      };
      result.pages.push(formattedPage);
      elements.forEach((element) => {
        result.elementsByUUID[element.uuid] = { // eslint-disable-line
          ...element,
          page: page.page_number
        }; // eslint-disable-line
      });
    }
    return result;
  }, { elementsByUUID: {}, pages: [] });
  const formattedCustomization = {
    ...customization,
    name: customization.name,
    elementsByUUID,
    pages: _.sortBy(pages, page => page.page_number), // pages in response are not guaranteed to be ordered
  };

  return formattedCustomization;
};

function orderMapper(order) {
  return {
    createdAt: order.created_at,
    orderStatus: order.order_status,
    splitOrderID: order.split_order_id,
    projects: order.projects.map(project => ({
      projectUUID: project.project_uuid,
      projectName: project.project_name,
      completedAt: project.completed_at,
      quantity: project.quantity,
      primaryCustomization: _.find(project.customizations, customization => customization.type === 'INVITATION' || customization.type === 'SAVE_THE_DATE'),
      orderUUID: project.split_order_id,
      customizations: project.customizations.map(c => ({
        customizationUUID: c.project_customization_uuid,
        type: c.type,
        name: c.name,
        quantity: c.quantity,
        renders: c.renders,
        displayOrder: c.display_order,
        imageUrls: c.sku_view.image_urls,
        skuId: c.sku_view.object_id,
        priceCents: c.sku_view.price_cents,
        color: c.sku_view.product_option_values.color,
        // colorLabel: c.sku_view.product_option_values['color-label'] || 'this is a color',
        colorLabel: c.sku_view.product_option_value_labels.color,
        paperType: c.sku_view.product_option_values['paper-type'],
        reversePrinting: c.sku_view.product_option_values['reverse-printing'],
        envelopeLiningColor: c.sku_view.product_option_value_labels['envelope-lining-color'],
        shape: c.sku_view.product_option_values.silhouette,
        size: c.sku_view.product_option_values.size
      }))
    }))
  };
}

const getFontStylesheets = (customizations, prevFontStylesheets = []) => {
  const fontStylesheets = [];
  try {
    _.chain(customizations)
      .flatMap(customization => customization.pages)
      .filter(page => page.elements && page.elements.length > 0)
      .flatMap(page => page.elements)
      .filter(element => element.body && element.body !== null)
      .reduce((result, element) => {
        const { font } = element;
        if (font.google_font) {
          result.push({
            fontFamily: font.font_family.replace(' ', '+'),
            fontWeight: font.font_weight,
            fontStyle: font.font_style.replace(' ', ''),
          });
        }
        return result;
      }, [])
      .uniqWith(_.isEqual)
      .forEach((font) => {
        const { fontFamily, fontWeight, fontStyle } = font;
        fontStylesheets.push({
          rel: 'stylesheet',
          href: `https://fonts.googleapis.com/css?family=${fontFamily}:${fontWeight}${fontStyle}`,
        });
      })
      .value();
  } catch (e) {
    console.log(e);
  }
  return _.uniqWith([...prevFontStylesheets, ...fontStylesheets], _.isEqual);
  // return [...prevFontStylesheets, ...fontStylesheets];
};


const initialState = {
  projectUUID: null,
  projectType: null,
  splitOrderId: null,
  customizations: {},
  skus: {},
  customizationTypeToUUID: {},
  fontStylesheets: [],
  activeField: null,
  isFetching: false,
  steps: {},
  size: null,
  busy: false,
  drafts: {
    drafts: [],
    groupedDrafts: {
      orderedNames: [],
      count: 0,
    }
  },
  orders: []
};

function cardProjectReducer(state = initialState, action = {}) {
  switch (action.type) {
    case ActionTypes.REQUEST_PROJECT: {
      return { ...state, isFetching: true };
    }
    case ActionTypes.RECEIVE_PROJECT: {
      const { cardProject } = action.payload;
      const { id, uuid, name, split_order_id: splitOrderId, account_id: accountId } = cardProject;
      const projectType = _.find(cardProject.customizations, { type: 'SAVE_THE_DATE' }) ? 'SAVE_THE_DATE' : 'INVITATION';
      const customizations = _.reduce(cardProject.customizations, (result, customization) => ({
        ...result,
        [customization.uuid]: customizationMapper(customization)
      }), {});
      const customizationTypeToUUID = _.reduce(cardProject.customizations, (result, customization) => {
        result[customization.type] = customization.uuid; // eslint-disable-line
        return result;
      }, {});

      const isPetite = (projectType === 'SAVE_THE_DATE') && (_.findIndex(cardProject.customizations, { type: 'INVITATION_ENVELOPE' }) > -1);
      // TODO: clean this up - find way to calculate navigation using customizations instead of using
      // hardcoded values
      let steps = [];
      if (projectType === 'INVITATION') {
        steps = invitationSteps;
      } else if (isPetite) {
        steps = petiteSaveTheDateSteps;
      } else {
        steps = postcardSaveTheDateSteps;
      }

      // TODO: clean this up - find a smarter way of determining card 'size'
      let size = null;
      if (projectType === 'INVITATION') {
        size = '5x7';
      } else if (isPetite) {
        size = 'petite';
      } else {
        size = 'postcard';
      }

      const fontStylesheets = getFontStylesheets(cardProject.customizations, state.fontStylesheets);

      return {
        ...state,
        id,
        projectUUID: uuid,
        projectType,
        name,
        splitOrderId,
        accountId,
        customizations,
        customizationTypeToUUID,
        fontStylesheets,
        isFetching: false,
        steps,
        size,
      };
    }
    case ActionTypes.REQUEST_CUSTOMIZATION: {
      return { ...state };
    }
    case ActionTypes.RECEIVE_CUSTOMIZATION: {
      const { customization } = action.payload;
      const fontStylesheets = getFontStylesheets([customization], state.fontStylesheets);
      // const nextFontStylesheets = _.uniqWith([...state.fontStylesheets, ...fontStylesheets], _.isEqual);
      // console.log(fontStylesheets);
      return {
        ...state,
        customizations: {
          ...state.customizations,
          [customization.uuid]: customizationMapper(customization),
        },
        customizationTypeToUUID: {
          ...state.customizationTypeToUUID,
          [customization.type]: customization.uuid
        },
        fontStylesheets,
      };
    }
    case ActionTypes.RECEIVE_CUSTOMIZATION_DISABLED: {
      const { uuid } = action.payload;
      console.log('RECEIVE_CUSTOMIZATION_UUID', uuid);
      return {
        ...state,
        customizations: {
          ...state.customizations,
          [uuid]: {
            ...state.customizations[uuid],
            deleted_at: true
          }
        }
      };
    }
    case ActionTypes.RECEIVE_SKU: {
      const { sku } = action.payload;
      return {
        ...state,
        skus: {
          ...state.skus,
          [sku.object_id]: sku
        }
      };
    }
    // TODO: this could be named in a more generic way - RECEIVE_ELEMENT or something similar
    case ActionTypes.RECEIVE_DELETE_ELEMENT_IMAGE: {
      const { customizationUUID, elementUUID, element } = action.payload;
      return {
        ...state,
        customizations: {
          ...state.customizations,
          [customizationUUID]: {
            ...state.customizations[customizationUUID],
            elementsByUUID: {
              ...state.customizations[customizationUUID].elementsByUUID,
              [elementUUID]: element
            }
          }
        }
      };
    }
    case ActionTypes.SET_PROJECT_QUANTITY: {
      const { quantity } = action.payload;
      const nextCustomizations = {};
      Object.keys(state.customizations).forEach((uuid) => {
        const customization = state.customizations[uuid];
        nextCustomizations[customization.uuid] = {
          ...customization,
          quantity
        };
      });
      return {
        ...state,
        customizations: nextCustomizations
      };
    }
    case ActionTypes.SET_ACTIVE_FIELD: {
      const { activeField } = action.payload;
      // console.log('activeField: ', activeField);
      return { ...state, activeField };
    }
    case ActionTypes.REQUEST_DRAFTS: {
      return {
        ...state,
        busy: true
      };
    }
    case ActionTypes.RECEIVE_DRAFTS: {
      const { drafts } = action.payload;
      const filteredDrafts = drafts.filter(draft => draft.customizations.length > 0);
      const formattedDrafts = filteredDrafts.map(p => ({
        projectUUID: p.project_uuid,
        projectName: p.project_name,
        customizations: p.customizations.sort((a, b) => orderedCustomizationTypes.indexOf(a.type) - orderedCustomizationTypes.indexOf(b.type)),
        primaryCustomization: _.find(p.customizations, customization => customization.type === 'INVITATION' || customization.type === 'SAVE_THE_DATE')
      }));
      const sortedDrafts = formattedDrafts.sort((a, b) => (
        moment(b.primaryCustomization.updated_at).valueOf() - moment(a.primaryCustomization.updated_at).valueOf()
      ));
      const nextDrafts = sortedDrafts.reduce((result, draft) => {
        result.count += 1; // eslint-disable-line
        if (_.has(result, draft.projectName)) {
          result[draft.projectName].push(draft);
        } else {
          result.orderedNames.push(draft.projectName);
          result[draft.projectName] = [draft]; // eslint-disable-line
        }
        return result;
      }, { orderedNames: [], count: 0 });

      return {
        ...state,
        // drafts,
        drafts: {
          ...state.drafts,
          drafts: formattedDrafts,
          groupedDrafts: nextDrafts,
        },
        busy: false,
      };
    }
    case ActionTypes.REQUEST_ORDERS: {
      return { ...state, busy: true };
    }
    case ActionTypes.RECEIVE_ORDERS: {
      const { orders } = action.payload;
      const formattedOrders = orders.map(order => orderMapper(order));
      const sortedOrders = formattedOrders.sort((a, b) => (
        moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
      ));

      return {
        ...state,
        orders: sortedOrders,
        busy: false };
    }
    case ActionTypes.RECEIVE_CANCELED_ORDER: {
      const { order } = action.payload;
      const formattedOrder = orderMapper(order);
      const updatedOrders = state.orders.map((currentOrder) => {
        if (currentOrder.splitOrderID === formattedOrder.splitOrderID) {
          return formattedOrder;
        }
        return currentOrder;
      });
      return { ...state, orders: updatedOrders };
    }
    default: {
      return { ...state };
    }
  }
}

export default cardProjectReducer;



/** WEBPACK FOOTER **
 ** ./src/cards/reducers/cardProject.js
 **/